import type { BookmarkDto } from '@mimir/common/dto/bookmarkDto';
import { BM_QUERY_PARAM, type BookmarkModel, type BookmarkViewModel } from '@mimir/common/dto/userSettingsModels';
import { getI18n } from '@mimir/common/i18n/initializeI18n';
import { getGlobalRouter } from '@mimir/web/router';
import eventBus, { EVENTS } from '../eventBus';
import { loadSystemBookmarks } from '../globals/bookmarks';
import noop from 'lodash/noop';
import type { User } from '../model/User';
import type { RawLocation } from 'vue-router';

export interface BookmarkTreeNodeLegacy {
  id: string;
  children: BookmarkTreeNodeLegacy[];
  bookmarkDto: BookmarkDto | BookmarkViewModel | undefined;
  isRootLevel: boolean;
  currentNodePath: string;
  title: string;
  icon: string;
  type: 'System' | 'Private';
  isHovered: boolean;
  isLoading: boolean;
}

export interface BookmarkTreeIndexMap {
  [path: string]: BookmarkTreeNodeLegacy;
}

export function getSanitizedFolderPath(path: string) {
  const pathSegments = path.split('/').filter(s => s.length > 0);
  return { sanitizedPath: `/${pathSegments.join('/')}`, pathSegments };
}

export class BookmarkTreeBuilder {
  private indexByPath: BookmarkTreeIndexMap = {};

  public getIndexByPath() {
    return this.indexByPath;
  }

  private createTreeNode(nodeInfo: Omit<BookmarkTreeNodeLegacy, 'children' | 'isLoading'>) {
    const node: BookmarkTreeNodeLegacy = {
      ...nodeInfo,
      children: [],
      isLoading: false,
      isHovered: nodeInfo.isHovered ?? false,
    };
    return node;
  }

  private createOrGetFolderNode(node: Pick<BookmarkTreeNodeLegacy, 'isRootLevel' | 'currentNodePath' | 'type'> & { title?: string }, id?: string) {
    const { sanitizedPath: nodePath, pathSegments } = getSanitizedFolderPath(node.currentNodePath);

    if (this.indexByPath[nodePath]) return this.indexByPath[nodePath];

    const folderNode = this.createTreeNode({
      ...node,
      bookmarkDto: undefined,
      icon: 'folder',
      id: id ?? nodePath,
      currentNodePath: nodePath,
      title: node.title ?? pathSegments[pathSegments.length - 1],
      isHovered: false
    });

    this.indexByPath[nodePath] = folderNode;

    const pathToLastSegment = `/${pathSegments.slice(0, pathSegments.length - 1).join('/')}`;
    if (pathToLastSegment !== nodePath) {
      if (this.indexByPath[pathToLastSegment] === undefined) {
        this.createOrGetFolderNode({ isRootLevel: false, currentNodePath: pathToLastSegment, type: node.type });
      }
      this.indexByPath[pathToLastSegment]?.children.push(folderNode);
    }

    return folderNode;
  }

  private createBookmarkNode(nodeInfo: Omit<BookmarkTreeNodeLegacy, 'children' | 'isLoading'>) {
    const bookmarkNode = this.createTreeNode(nodeInfo);
    if (bookmarkNode.currentNodePath) {
      const parent = this.createOrGetFolderNode({
        isRootLevel: false,
        currentNodePath: bookmarkNode.currentNodePath,
        type: bookmarkNode.type
      });
      parent.children.push(bookmarkNode);
    }
  }

  public loadBookMarkTree(dtos: (BookmarkDto | BookmarkViewModel)[], type: BookmarkTreeNodeLegacy['type']) {
    const bookmarkTreeRoot = this.createOrGetFolderNode({
      title: getI18n().t('Bookmarks.Type', { type: type }),
      isRootLevel: true,
      currentNodePath: '/',
      type
    }, `${type}-bookmarks`);

    for (const bmk of dtos) {
      this.createBookmarkNode({
        id: bmk.id,
        bookmarkDto: bmk,
        isRootLevel: false,
        currentNodePath: bmk.folderTreePath ?? '/',
        title: bmk.title,
        icon: bmk.icon,
        type,
        isHovered: false
      });
    }

    return bookmarkTreeRoot;
  }
}

export function gotoBookmark(bookmark: BookmarkModel, browseMode: boolean) {
  let path = bookmark.path;
  const router = getGlobalRouter();

  if (bookmark.isFilterBookmark) {
    const currentUrl = new URL(window.location.href);
    const currentParams = new URLSearchParams(currentUrl.search);
    const bookmarkParams = new URLSearchParams(bookmark.path.split('?')[1]);
    bookmarkParams.forEach((value, key) => {
      if (key !== 'folderId' && key !== 'viewType') {
        currentParams.set(key, value); // Replace filters, but keep folderId and viewType intact
      }
    });
    path = `${currentUrl.pathname}?${currentParams.toString()}`;
  } else if (browseMode) {
    const folderSearchPrefix = '/folders?';
    const normalSearchPrefix = '/?';
    const noSearchPrefix = '/';
    let substringStartsFrom: number | undefined;
    if (bookmark.path.startsWith(folderSearchPrefix)) {
      substringStartsFrom = folderSearchPrefix.length;
    } else if (bookmark.path.startsWith(normalSearchPrefix)) {
      substringStartsFrom = normalSearchPrefix.length;
    } else if (bookmark.path === noSearchPrefix) {
      // if the path only contains "/" then we shall omit that.
      substringStartsFrom = noSearchPrefix.length;
    }
    path = `/browse?${!substringStartsFrom ? bookmark.path : bookmark.path.substring(substringStartsFrom)}`;
  }

  if (router.currentRoute.query[BM_QUERY_PARAM] === bookmark.id) {
    eventBus.emit(EVENTS.REFRESH_CURRENT_SEARCH);
  } else {
    path = preparePathWithBookmarkId(path, bookmark.id);
    void router.push({ path }).catch(noop);
  }
}

/**
 * Navigate to a default bookmark by ID. If the bookmark is not found, this function
 * will silently fail and return false.
 */
async function gotoDefaultBookmark(bookmarkId: string, browseMode: boolean = false): Promise<boolean> {
  const { bookmarks } = await loadSystemBookmarks({ useCache: true, loadFolders: false });
  const bookmark = bookmarks.find(b => b.id === bookmarkId);

  if (!bookmark) {
    console.warn(`Default bookmark with ID ${bookmarkId} not found`);
    return false;
  }

  gotoBookmark(bookmark, browseMode);
  return true;
}

/**
 * Try to navigate to the user's primary group default bookmark
 * @param user - The current user
 * @returns true if navigated to default bookmark, false otherwise
 */
export async function tryNavigateToDefaultBookmark(user: User): Promise<boolean> {
  const bookmarkId = user.primaryGroupDefaultBookmarkId;

  if (bookmarkId) {
    return await gotoDefaultBookmark(bookmarkId);
  }

  return false;
}


/**
 * Get the RawLocation for the user's primary group default bookmark.
 * @param user - The current user
 * @returns RawLocation if default bookmark exists and is found, void otherwise
 */
export async function tryGetDefaultBookmarkLocation(user: User): Promise<RawLocation | void> {
  const bookmarkId = user.primaryGroupDefaultBookmarkId;

  if (!bookmarkId) {
    return;
  }

  const { bookmarks } = await loadSystemBookmarks({ useCache: true, loadFolders: false });
  const bookmark = bookmarks.find(b => b.id === bookmarkId);

  if (!bookmark) {
    console.warn(`Default bookmark with ID ${bookmarkId} not found`);
    return;
  }

  const path = preparePathWithBookmarkId(bookmark.path, bookmark.id);
  return { path };
}

/**
 * Get the user's default bookmark if configured
 * @param user - The current user
 * @returns BookmarkModel if found, null otherwise
 */
export async function getDefaultBookmark(user: User): Promise<BookmarkModel | null> {
  const bookmarkId = user.primaryGroupDefaultBookmarkId;

  if (!bookmarkId) {
    return null;
  }

  try {
    const { bookmarks } = await loadSystemBookmarks({ useCache: true, loadFolders: false });
    const bookmark = bookmarks.find(b => b.id === bookmarkId);
    return bookmark ?? null;
  } catch (error) {
    console.error(`Failed to load default bookmark ${bookmarkId}:`, error);
    return null;
  }
}

/**
 * Check if current route matches a bookmark's path
 * @param currentPath - Current route path
 * @param currentQuery - Current route query params
 * @param bookmark - Bookmark to check against
 * @returns true if current route matches bookmark
 */
export function isAtBookmarkLocation(currentPath: string, currentQuery: Record<string, any>, bookmark: BookmarkModel): boolean {
  const bookmarkUrl = new URL(bookmark.path, window.location.origin);
  const bookmarkPath = bookmarkUrl.pathname;
  const bookmarkQuery = Object.fromEntries(bookmarkUrl.searchParams.entries());

  if (currentPath !== bookmarkPath) {
    return false;
  }

  // Parameters to exclude from comparison (UI state, not bookmark content)
  const excludedParams = new Set(['ts', BM_QUERY_PARAM, 'includeSubfolders']);

  // NOTE: Filter bookmarks can no longer be set as default home page locations.
  // However, this check is retained for backward compatibility with legacy data - users who
  // set filter bookmarks as home before the restriction may still have them configured.
  if (bookmark.isFilterBookmark) {
    // For filter bookmarks, check if key filter params match
    // Also exclude viewType and folderId as they are preserved from current state
    // (see gotoBookmark function which preserves these params for filter bookmarks)
    const filterBookmarkExcluded = new Set([...excludedParams, 'viewType', 'folderId']);
    const filterKeys = Object.keys(bookmarkQuery).filter(k => !filterBookmarkExcluded.has(k));

    for (const key of filterKeys) {
      if (currentQuery[key] !== bookmarkQuery[key]) {
        return false;
      }
    }
  } else {
    // For normal bookmarks, all query params must match exactly (except excluded params)
    const filterKeys = Object.keys(bookmarkQuery).filter(k => !excludedParams.has(k));

    for (const key of filterKeys) {
      if (currentQuery[key] !== bookmarkQuery[key]) {
        return false;
      }
    }

    // Also check that current doesn't have extra params that bookmark doesn't have
    const currentKeys = Object.keys(currentQuery).filter(k => !excludedParams.has(k));
    for (const key of currentKeys) {
      if (!(key in bookmarkQuery)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * For backwards compatibility, we do not save the bookmark id with new/old bookmarks but
 * rather inject it when a user is navigating to one.
 * @param path The bookmark path
 * @param bookmarkId The id to include in the path
 * @returns a path with a query parameter referencing a bookmark
 */
export function preparePathWithBookmarkId(path: string, bookmarkId: string): string {
  let lastIndex = path.indexOf('?');
  if (lastIndex < 0) {
    lastIndex = path.length;
  }
  const substr = path.substring(0, lastIndex);
  const withoutprefix = path.substring(lastIndex);
  const params = new URLSearchParams(withoutprefix);

  // Ensure we only have one reference
  params.delete(BM_QUERY_PARAM);
  params.append(BM_QUERY_PARAM, bookmarkId);
  return `${substr}?${params}`;
}
